import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@moe/oss/ui/card";
import { Character } from "@moe/priv/model/character";
import { cn } from "@moe/priv/utils";
import { useQuery } from "@tanstack/react-query";
import { SearchBar } from "@web/components/search/SearchBar";
import { SearchDescription } from "@web/components/search/SearchDescription";
import { useParseCharacterSearch } from "@web/components/search/use-parse-character-search";
import { Action, ThinCharacterCard } from "@web/components/ThinCharacterCard";
import { useRecentCharacterQuery } from "@web/route-services/chat/use-recent-characters-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

interface ManageCharactersViewProps {
  charactersInChat: Character[];
  onCharacterAdd: (character: Character) => void;
  maxCharacters: number;
  title: string;
  description?: string;
  getCharacterActions?: (char: Character) => Action[];
  action?: React.ReactNode;
  className?: string;
}

interface SearchQuery {
  text: string;
  tags: string[];
}

export function ManageCharactersView({
  charactersInChat,
  onCharacterAdd,
  maxCharacters,
  title,
  description,
  getCharacterActions,
  action,
  className
}: ManageCharactersViewProps) {
  const { access } = useAppContext();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    text: "",
    tags: []
  });

  const { data: searchedCharacters, isPending } = useQuery({
    queryKey: ["searchedCharacters", searchQuery],
    queryFn: async () => {
      return await access.character.getPage({
        page: 0,
        pageSize: 35,
        ranking: "message_count",
        order: "desc",
        filter: {
          searchText: searchQuery.text,
          tags: searchQuery.tags,
          privateOK: true,
          nsfwOK: true
        }
      });
    }
  });

  const { data: recentCharacters } = useRecentCharacterQuery();

  const filteredRecentCharacters = recentCharacters?.filter(
    (recentChar) => !charactersInChat.some((char) => char.id === recentChar.id)
  );

  const displayedCharacters =
    searchQuery.text || searchQuery.tags.length > 0 ? (searchedCharacters ?? []) : (filteredRecentCharacters ?? []);

  const { parseSearch } = useParseCharacterSearch();

  const handleSearch = (query: string) => {
    const result = parseSearch(query);
    if (result.type === "link") return;
    setSearchQuery({
      text: result.text,
      tags: result.tags
    });
  };

  const handleClearSearch = () => {
    setSearchQuery({ text: "", tags: [] });
  };

  return (
    <div className="relative">
      <div className="from-float pointer-events-none absolute bottom-0 left-0 z-10 h-12 w-full rounded-b-md bg-gradient-to-t to-transparent md:h-9" />

      <Card
        className={cn(
          "text-tx-primary bg-float scroll-hidden relative h-[42rem] w-full overflow-auto border-none pb-20 md:w-[35rem] md:border md:pb-0 ",
          className
        )}
      >
        <CardHeader className="px-4 md:px-10">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>

        <CardContent className="relative px-4 md:px-10">
          <SearchBar
            searchOnBlur={true}
            onClear={handleClearSearch}
            placeholder="Search..."
            onSearch={handleSearch}
            className="bg-input-primary mb-6 h-12 w-full shrink-0"
          />

          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Characters In Chat</h3>
              <span className="text-tx-tertiary font-mono text-sm font-bold">
                {`${charactersInChat.length}/${maxCharacters}`}
              </span>
            </div>

            <div className="mt-2 flex w-full flex-col space-y-0">
              {charactersInChat.length === 0 && (
                <div className="text-tx-tertiary flex flex-col items-center py-8">
                  <span className="text-lg font-semibold">No characters added yet (｡◕‿◕｡)</span>
                  <span className="text-sm font-semibold">Add characters from below ↓</span>
                </div>
              )}
              <AnimatePresence initial={false}>
                {charactersInChat?.map((char) => (
                  <motion.div
                    key={char.id}
                    initial={{ opacity: 0, transform: "translateY(20px)" }}
                    animate={{ opacity: 1, transform: "translateY(0px)" }}
                    exit={{ opacity: 0, transform: "translateX(-50px)", height: "0" }}
                    transition={{
                      ease: "easeIn",
                      opacity: { duration: 0.2 },
                      transform: { duration: 0.25 }
                    }}
                  >
                    <ThinCharacterCard
                      key={char.id}
                      className="pl-0 active:bg-inherit"
                      char={char}
                      showContextMenu={true}
                      showDropdownMenu={true}
                      openDropdownOnClick={true}
                      actions={getCharacterActions?.(char)}
                    />
                  </motion.div>
                ))}
                <div className="mb-6">{action}</div>
              </AnimatePresence>
            </div>
          </div>

          {/* Search/Recent Section */}
          <div className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold">
              {searchQuery.text || searchQuery.tags.length > 0 ? "Search Results" : "Recents"}
            </h3>
            <SearchDescription
              className=""
              searchText={searchQuery.text}
              tags={searchQuery.tags}
              onClear={handleClearSearch}
            />
            <div className="flex w-full flex-col space-y-2">
              {displayedCharacters?.length === 0 ? (
                <div className="text-tx-tertiary flex flex-col items-center py-8">
                  {searchQuery.text || searchQuery.tags.length > 0 ? (
                    <span className="text-lg">No characters found (´｡• ω •｡`)</span>
                  ) : (
                    <span className="text-lg">No recent characters yet (✿◠‿◠)</span>
                  )}
                </div>
              ) : (
                displayedCharacters?.map((char) => (
                  <ThinCharacterCard
                    className="pl-0 active:bg-inherit"
                    key={char.id}
                    char={char}
                    showContextMenu={false}
                    showDropdownMenu={false}
                    onClick={() => onCharacterAdd(char)}
                  />
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
